import { useTranslation } from "react-i18next";
import { ErrorState } from "@/components/shared/error-state";
import { SmartCreationResult } from "@/components/smart-creation/SmartCreationResult";
import { SmartCreationWorkspace } from "@/components/smart-creation/SmartCreationWorkspace";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { readSmartCreationPendingDraft } from "@/lib/smart-creation-draft-store";
import type { Shop } from "@/stores/auth";
import type { CampaignSmartCreation } from "./use-campaign-smart-creation";

/**
 * Full-page Smart Creation surface that replaces the campaign list while the
 * URL carries a plan or creation task.
 */
export function CampaignSmartCreationView({
  smartCreation,
  canEdit,
  shops,
  activeWorkspaceId,
}: {
  smartCreation: CampaignSmartCreation;
  canEdit: boolean;
  shops: Shop[];
  activeWorkspaceId: string | null | undefined;
}) {
  const { t: smartT } = useTranslation("smartCreation");
  const { creationTaskId, planId, shopId, identityQuery } = smartCreation;
  if (creationTaskId) {
    const localDraft = readSmartCreationPendingDraft(creationTaskId);
    return (
      <SmartCreationResult
        taskId={creationTaskId}
        canMutate={canEdit}
        onClose={() => void smartCreation.close()}
        onResumeDraft={
          localDraft ? () => void smartCreation.resumeDraft(localDraft.plan.id) : undefined
        }
      />
    );
  }
  if (
    planId &&
    shopId &&
    shops.some((shop) => shop.shopId === shopId && shop.workspaceId === activeWorkspaceId)
  ) {
    return (
      <SmartCreationWorkspace
        open
        planId={planId}
        shopId={shopId}
        shopName={shops.find((shop) => shop.shopId === shopId)?.shopName ?? "—"}
        canEdit={canEdit}
        shopChanging={smartCreation.switchShopMutation.isPending}
        onShopChange={smartCreation.changeShop}
        onClose={smartCreation.close}
        onTaskAccepted={(task) => smartCreation.acceptTask(task.id)}
      />
    );
  }
  return (
    <div className="space-y-4 p-6">
      <Button variant="ghost" size="sm" onClick={() => void smartCreation.close()}>
        {smartT("actions.back")}
      </Button>
      {identityQuery.isError ? (
        <ErrorState message={smartT("errors.load")} onRetry={() => void identityQuery.refetch()} />
      ) : shopId ? (
        // The plan resolved, but its Shop is not one of the active Workspace's Shops.
        <ErrorState message={smartT("errors:shop.forbidden")} />
      ) : (
        <Skeleton className="h-96 w-full" />
      )}
    </div>
  );
}
